import { defineMessages } from '../../i18n/messages';
import { englishText } from '../../utils/messageFallbacks';

const DEFINITIONS = {
  HEADING: {
    id: 'pricing.calculator.heading',
    text: 'Which plan do I need?',
    description: 'Heading for the interactive plan calculator on the pricing page.',
  },
  LEDE: {
    id: 'pricing.calculator.lede',
    text: 'Usage is billed as source words multiplied by the number of languages you translate into. That makes the number of languages, not the size of your app, the biggest factor in what you pay.',
    description: 'Introduction to the plan calculator. Explains the billing formula in plain words: total usage equals the word count of your source language multiplied by how many target languages are selected.',
  },
  LABEL_SOURCE_WORDS: {
    id: 'pricing.calculator.label_source_words',
    text: 'Words in your source language',
    description: 'Label for the number input where the visitor enters how many words of text their app contains in its original language.',
  },
  LABEL_LOCALES: {
    id: 'pricing.calculator.label_locales',
    text: 'Languages to translate into',
    description: 'Label for the number input where the visitor enters how many target languages they want.',
  },
  HINT_SOURCE_WORDS: {
    id: 'pricing.calculator.hint_source_words',
    text: 'The Localingos site itself is about 5,200 words across 899 strings.',
    description: 'Hint under the source words input, giving a real reference point so the visitor can estimate their own app. Refers to the Localingos website\'s own interface text.',
  },
  RESULT_TOTAL: {
    id: 'pricing.calculator.result_total',
    text: '{{sourceWords}} words into {{locales}} languages is {{total}} billable words.',
    description: 'Result line stating the computed total. {{sourceWords}} is the entered source word count, {{locales}} the number of target languages, {{total}} the product of the two.',
  },
  RESULT_PLAN: {
    id: 'pricing.calculator.result_plan',
    text: 'The {{plan}} plan covers that.',
    description: 'Result line naming the cheapest plan whose yearly allowance covers the computed total. {{plan}} is the plan name, such as Free, Starter, Growth or Scale.',
  },
  RESULT_PLAN_FREE: {
    id: 'pricing.calculator.result_plan_free',
    text: 'The free tier covers that, with no card required.',
    description: 'Result line shown when the computed total fits within the free allowance.',
  },
  RESULT_OVER_LARGEST: {
    id: 'pricing.calculator.result_over_largest',
    text: 'That is beyond the largest plan. Get in touch and we will work out the right size.',
    description: 'Result line shown when the computed total exceeds even the largest plan\'s yearly allowance, inviting the visitor to contact the team.',
  },
  FIRST_SYNC_NOTE: {
    id: 'pricing.calculator.first_sync_note',
    text: 'This is your first sync, which is the expensive one: every string is new, so every string is billed against every language. After that only the strings you change are re-translated, so ongoing use is a small fraction of it.',
    description: 'Explanatory note clarifying that the calculated figure represents the initial full translation, and that subsequent usage is much lower because only modified strings are re-translated.',
  },
  CONTACT_LINK: {
    id: 'pricing.calculator.contact_link',
    text: 'Contact us',
    description: 'Link text to the contact page, shown when the required volume exceeds all plans.',
  },
} as const;

export const CalculatorMessages = defineMessages(DEFINITIONS);
export const CalculatorText = englishText(DEFINITIONS);
